import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCycle } from "../../context/CycleContext";
import { getPerformanceResults } from "../../services/resultApi";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, DataTable, PersonCell, StatusBadge, SearchInput, Tabs, ErrorState, Banner, OrgFilters } from "../../components/common";
import RatingPill from "../../components/performance/RatingPill";
import { RESULT_STATUS } from "../../utils/constants";

import { downloadCsv } from "../../utils/exportCsv";
import { downloadXlsx } from "../../utils/exportExcel";
import Button from "../../components/common/Button";
import { FiDownload } from "react-icons/fi";
import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = { getPerformanceResults };

export default function HrReviewPage() {
  const { cycleId, cycle, isStageOpen } = useCycle();
  const navigate = useNavigate();
  const [tab, setTab] = useState(RESULT_STATUS.PENDING_HR);
  const [search, setSearch] = useState("");
  const org = useOrgFilters(); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const { data, loading, error, reload } = useAsync(() => api.getPerformanceResults(cycleId, { search }), [cycleId, search], { skip: !cycleId });

  if (error) return <ErrorState message={error} onRetry={reload} />;
  const all = (data || []).filter((r) => org.matches(r.employee));
  const count = (s) => all.filter((r) => r.resultStatus === s).length;
  const rows = tab === "all" ? all : all.filter((r) => r.resultStatus === tab);

  const exportColumns = [
    { key: "id", label: "Employee ID", value: (r) => r.employee.id },
    { key: "name", label: "Name", value: (r) => r.employee.name },
    { key: "bu", label: "Business Unit", value: (r) => org.directory[r.employee.id]?.businessUnit || r.employee.businessUnit },
    { key: "dept", label: "Department", value: (r) => r.employee.departmentName },
    { key: "loc", label: "Location", value: (r) => org.directory[r.employee.id]?.location || r.employee.location },
    { key: "cc", label: "Cost Centre", value: (r) => org.directory[r.employee.id]?.costCenter || r.employee.costCenter },
    { key: "le", label: "Legal Entity", value: (r) => org.directory[r.employee.id]?.legalEntity || r.employee.legalEntity },
    { key: "mgr", label: "Manager", value: (r) => r.employee.managerName },
    { key: "sheet", label: "Goal sheet", value: (r) => r.sheetStatus },
    { key: "self", label: "Self assessment", value: (r) => r.selfStatus },
    { key: "mgrStatus", label: "Manager assessment", value: (r) => r.managerStatus },
    { key: "goal", label: "Goal score", value: (r) => r.performance.goalScore ?? "—" },
    { key: "kra", label: "KRA score", value: (r) => r.performance.kraScore ?? "—" },
    { key: "final", label: "Final score", value: (r) => r.performance.finalScore ?? "—" },
    { key: "rating", label: "Rating", value: (r) => (r.resultStatus === RESULT_STATUS.NOT_READY ? "Not Yet Rated" : r.performance.rating) },
    { key: "status", label: "Result", value: (r) => r.resultStatus },
  ];
  const exportName = `hr-review-${cycle?.name}`;
  const exportExcel = () =>
    downloadXlsx(exportName, [{ name: "Review & finalize", title: `Employee performance — ${cycle?.name}`, columns: exportColumns, rows }]);
  const exportRows = () => downloadCsv(exportName, exportColumns, rows);

  return (
    <div className="page">
      <PageHeader
        title="Review & finalize"
        subtitle={`Check calculated ratings and lock appraisals for ${cycle?.name}.`}
        actions={
          <>
            <Button variant="ghost" icon={FiDownload} onClick={exportRows}>CSV</Button>
            <Button variant="secondary" icon={FiDownload} onClick={exportExcel}>Export Excel</Button>
          </>
        }
      />
      {!isStageOpen("hrFinalization") && (
        <Banner tone="neutral" title="HR finalization is not open">Open the stage under Performance cycles to finalize appraisals.</Banner>
      )}
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: RESULT_STATUS.PENDING_HR, label: "Ready to finalize", count: count(RESULT_STATUS.PENDING_HR) },
          { key: RESULT_STATUS.NOT_READY, label: "Awaiting assessments", count: count(RESULT_STATUS.NOT_READY) },
          { key: RESULT_STATUS.FINALIZED, label: "Finalized", count: count(RESULT_STATUS.FINALIZED) },
          { key: "all", label: "Everyone", count: all.length },
        ]}
      />
      <div className="filter-bar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search name or ID" />
        <OrgFilters org={org} />
      </div>
      <Card padded={false}>
        <DataTable exportable={false}
          loading={loading && !data}
          rows={rows}
          rowKey={(r) => r.employee.id}
          onRowClick={(r) => navigate(`/hr/review/${r.employee.id}`)}
          emptyTitle="No one in this list"
          columns={[
            { key: "name", label: "Employee", render: (r) => <PersonCell name={r.employee.name} meta={`${r.employee.departmentName}, reports to ${r.employee.managerName}`} /> },
            { key: "sheet", label: "Goals", render: (r) => <StatusBadge status={r.sheetStatus} /> },
            { key: "self", label: "Self", render: (r) => <StatusBadge status={r.selfStatus} /> },
            { key: "mgr", label: "Manager", render: (r) => <StatusBadge status={r.managerStatus} /> },
            { key: "score", label: "Score", align: "right", render: (r) => formatRatingScore(r.performance.finalScore) },
            { key: "rating", label: "Rating", render: (r) => <RatingPill rating={r.resultStatus === RESULT_STATUS.NOT_READY ? null : r.performance.rating} tone={r.performance.ratingTone} size="sm" /> },
            { key: "status", label: "Result", render: (r) => <StatusBadge status={r.resultStatus} /> },
          ]}
        />
      </Card>
    </div>
  );
}
